/* Import components */

import React, {useEffect, useRef, useState} from 'react';
import {Field, useFormikContext} from 'formik';
import {filter as _filter} from 'lodash';
import { useLocation } from 'react-router-dom';
import {useHistory} from 'react-router';
import {useSelector} from 'react-redux';
import {Form, DataTable} from '../components';
import Select from 'react-select';
import {languages, countries} from '../config';
import {axios} from '../libs';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import ClassicEditor from 'ckeditor5-custom-build';
import {SortableContainer, SortableElement, sortableHandle } from 'react-sortable-hoc';
import {arrayMoveImmutable} from 'array-move';
import {InputComponent} from '../components/ChatbotBuilder/Components';
import {roles} from '../config';
import {User} from '../states/actions';
import UpdateLawyersDataButton from '../components/UpdateLawyersDataButton';
import UpdateWordpressDataButton from '../components/UpdateWordpressDataButton';
import logger from '../utils/logger';
import { useTranslation } from '../i18n/I18nContext';
import { LEGAL_FIELD_CATEGORIES } from '../constants/legalFieldCategories';

const EXPERTISE_NOTE_MIN = 40;
const EXPERTISE_NOTE_MAX = 120;

function isExpertiseNoteValid(note) {
	const len = String(note || '').trim().length;
	return len === 0 || (len >= EXPERTISE_NOTE_MIN && len <= EXPERTISE_NOTE_MAX);
}

function normalizeExpertiseForSave(expertise) {
	return (expertise || []).map((item) => ({
		field_id: Number(item.field_id),
		note: String(item.note || '').trim(),
	}));
}

function notifyError(text) {
	window.nError?.({
		title: 'Error!',
		text,
		icon: 'fa-exclamation-triangle',
	});
}

/* Format date */

function formatDateDMY(date) {
	if (!date) return '';
	const d = new Date(date);
	const day = d.getDate().toString().padStart(2, '0');
	const month = (d.getMonth() + 1).toString().padStart(2, '0');
	const year = d.getFullYear();
	return `${day}.${month}.${year}`;
}

/* Sortable item */

const DragHandle = sortableHandle(() => <span> : : </span>);

const SortableItem = SortableElement(({ value, handleInputChange, remove }) => (
	<div>
		<DragHandle />
		<input
		type         = "text"
		defaultValue = {value}
		onBlur       = {handleInputChange}
		/>
		<button onClick={remove}>Remove</button>
	</div>
));

const SortableList = SortableContainer(({ items, handleInputChange, remove }) => (
	<ul>
		{items.map((item, index) => (
		<SortableItem
		key               = {item.id}
		index             = {index}
		value             = {item.value}
		handleInputChange = {(e) => handleInputChange(e, index)}
		remove            = {(e) => remove(item.id)}
		/>
		))}
	</ul>
));

// Custom submit button component that can access Formik context
const CustomSubmitButton = ({ newUser, closeModal, editingUserId, setEmailError, validationErrors, setValidationErrors, matchProfile }) => {
	const { values, submitForm } = useFormikContext();
	const { t } = useTranslation();
	
	const handleSubmit = async (e) => {
		e.preventDefault();
		
		// Clear any previous errors
		setEmailError('');
		setValidationErrors({});
		
		// Validate required fields (same as profile view)
		const validationErrors = {};
		
		// Validate firstName (required)
		if (!values.firstName || !values.firstName.trim()) {
			validationErrors.firstName = 'First Name is required';
		}
		
		// Validate lastName (required)
		if (!values.lastName || !values.lastName.trim()) {
			validationErrors.lastName = 'Last Name is required';
		}
		
		// Validate email (required)
		if (!values.email || !values.email.trim()) {
			validationErrors.email = 'Email is required';
		} else {
			// Validate email format
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(values.email.trim())) {
				validationErrors.email = 'Please enter a valid email address';
			}
		}
		
		// Validate role (required)
		if (!values.role) {
			validationErrors.role = 'Role is required';
		}
		
		// Validate company information fields (required for lawyers and admins)
		if (values.role === 'lawyer' || values.role === 'admin') {
			// Company Name is required
			if (!values.partner_company_name || !values.partner_company_name.trim()) {
				validationErrors.partner_company_name = 'Company Name is required';
			}
			
			// Registration Code is required
			if (!values.partner_company_reg_id || !values.partner_company_reg_id.trim()) {
				validationErrors.partner_company_reg_id = 'Registration Code is required';
			}
			
			// Company Address is required
			if (!values.partner_company_address || !values.partner_company_address.trim()) {
				validationErrors.partner_company_address = 'Company Address is required';
			}
			
			// IBAN is required
			if (!values.partner_IBAN || !values.partner_IBAN.trim()) {
				validationErrors.partner_IBAN = 'IBAN is required';
			}
			
			// Hourly Rate is required
			if (!values.partner_hourly_rate || !values.partner_hourly_rate.trim()) {
				validationErrors.partner_hourly_rate = 'Hourly Rate is required';
			}
		}
		
		// Validate discount price
		if (values.partner_discount_price && values.partner_hourly_rate) {
			const discountNum = parseFloat(values.partner_discount_price);
			const hourlyNum = parseFloat(values.partner_hourly_rate);
			
			if (!isNaN(discountNum) && !isNaN(hourlyNum) && hourlyNum > 0) {
				if (discountNum >= hourlyNum / 2) {
					validationErrors.partner_discount_price = 'Discount price must be less than half of the hourly rate';
				}
			}
		}
		
		// Validate business client pricing if enabled
		if (values.business_client && (values.role === 'lawyer' || values.role === 'admin')) {
			if (!values.business_hourly_rate || !values.business_hourly_rate.trim()) {
				validationErrors.business_hourly_rate = 'Hourly rate for businesses is required';
			}
		}
		
		// Validate business discount price
		if (values.business_discount_price && values.business_hourly_rate) {
			const discountNum = parseFloat(values.business_discount_price);
			const hourlyNum = parseFloat(values.business_hourly_rate);
			
			if (!isNaN(discountNum) && !isNaN(hourlyNum) && hourlyNum > 0) {
				if (discountNum >= hourlyNum / 2) {
					validationErrors.business_discount_price = 'Discount price must be less than half of the hourly rate';
				}
			}
		}
		
		// Validate VAT ID is required if VAT is checked
		if (values.partner_vat && (!values.partner_company_vat_id || values.partner_company_vat_id.trim() === '')) {
			validationErrors.partner_company_vat_id = 'VAT ID is required when VAT is enabled';
		}
		
		// Validate matchmaking requirements if active and role is lawyer
		if (values.role === 'lawyer' && values.is_active_for_matching) {
			if (!values.legal_fields || values.legal_fields.length === 0) {
				validationErrors.is_active_for_matching = 'At least 1 legal field must be selected';
			} else if (!values.services_offered || values.services_offered.length === 0) {
				validationErrors.is_active_for_matching = 'At least 1 service type must be selected';
			} else if (!values.languages || values.languages.length === 0) {
				validationErrors.is_active_for_matching = 'At least 1 language must be selected';
			} else if (!values.office_locations || values.office_locations.trim() === '') {
				validationErrors.is_active_for_matching = 'At least 1 office location must be selected';
			}
		}

		if (values.role === 'lawyer') {
			const expertise = normalizeExpertiseForSave(matchProfile?.expertise);
			if (expertise.some((item) => !isExpertiseNoteValid(item.note))) {
				validationErrors.expertise = t('match.expertiseNoteLengthError');
			}
		}
		
		if (Object.keys(validationErrors).length > 0) {
			setValidationErrors(validationErrors);
			if (validationErrors.expertise) {
				notifyError(validationErrors.expertise);
			}
			return;
		}
		
		// Validate email before submission
		if (values.email && values.email.trim()) {
			try {
				const data = await axios.get(`/user/check-email?email=${encodeURIComponent(values.email)}${editingUserId ? `&excludeId=${editingUserId}` : ''}`);
				
				logger.log('API response:', data); // Debug log
				
				if (data && data.exists) {
					setEmailError('This email is already in use');
					return; // Stop submission
				}
			} catch (error) {
				logger.error('Email validation error:', error);
				setEmailError('Error validating email');
				return; // Stop submission
			}
		}
		
		// If validation passes, submit the form
		submitForm();
	};
	
	return (
		<div className="form-row col-full row-actions">
			<button type="button" className="button button-large" onClick={closeModal}>Cancel</button>
			<button 
				type="button" 
				className="button button-large button-blue"
				onClick={handleSubmit}
			>
				{newUser ? 'Create' : 'Update'}
			</button>
		</div>
	);
};

export const Users = () => {
	const { t } = useTranslation();
	const [newUser, setNewUser] = useState(true);
	const [editingUserId, setEditingUserId] = useState(null);
	const [tooltip,setTooltip]  = useState({visible:false,text:'',x:0,y:0});
	const [errorMessage, setErrorMessage] = useState('');
	const [emailError, setEmailError] = useState('');
	const [legalFields, setLegalFields] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const [search, setSearch] = useState('');
	const [matchProfile, setMatchProfile] = useState({
		expertise: [],
		feedback_rating: '',
		feedback_rating_count: '',
		feedback_rating_source: 'sync',
		feedback_rating_synced_at: null,
	});
	const matchProfileRef = useRef(matchProfile);
	matchProfileRef.current = matchProfile;
	
	/* Search effect */
	useEffect(() => {
		setrefetch(true);
	}, [search]);
	
	/* Modal handlers */
	
	const [isModalOpen, setIsModalOpen] = useState(false);
	
	// Helper function to reset form fields to empty values
	const resetFormFields = () => {
		setinitialValues({
			email: '',
			role: '',
			firstName: '',
			lastName: '',
			picture_url: '',
			partner_company_name: '',
			partner_IBAN: '',
			partner_company_address: '',
			partner_hourly_rate: '',
			partner_discount_price: '',
			partner_vat: false,
			partner_company_reg_id: '',
			partner_company_vat_id: '',
			// Business client pricing
			business_client: false,
			business_hourly_rate: '',
			business_discount_price: '',
			// Matchmaking fields
			legal_fields: [],
			services_offered: [],
			languages: [],
			office_locations: '',
			is_active_for_matching: false,
			lawyer_level: 'pro',
		});
	};
	
	const openModal                     = () => { setIsModalOpen(true); };
	const closeModal                    = () => { 
		setIsModalOpen(false); 
		setEditingUserId(null);
		setNewUser(true);
		resetFormFields();
		setErrorMessage(''); // Clear error message when closing modal
		setEmailError(''); // Clear email error
		setMatchProfile({
			expertise: [],
			feedback_rating: '',
			feedback_rating_count: '',
			feedback_rating_source: 'sync',
			feedback_rating_synced_at: null,
		});
	};
	const addUser                       = () => {
		setNewUser(true);
		setEditingUserId(null);
		resetFormFields();
		setErrorMessage(''); // Clear error message when adding new user
		setEmailError(''); // Clear email error
		openModal();
		//push('/users');
	};
	
	/* Updated initialValues include partner details and new fields */
	
	// partner_vat is now a boolean (default false)
	
	const [initialValues, setinitialValues] = useState({
		email: '',
		role: '',
		firstName: '',
		lastName: '',
		picture_url: '',
		partner_company_name: '',
		partner_IBAN: '',
		partner_company_address: '',
		partner_hourly_rate: '',
		partner_discount_price: '',
		partner_vat: false,
		partner_company_reg_id: '',
		partner_company_vat_id: '',
		// Business client pricing
		business_client: false,
		business_hourly_rate: '',
		business_discount_price: '',
		// Matchmaking fields
		legal_fields: [],
		services_offered: [],
		languages: [],
		office_locations: '',
		is_active_for_matching: false,
		lawyer_level: 'pro',
	});
	
	const {push}                = useHistory();
	const [refetch, setrefetch] = useState(false);
	
	useEffect(() => {
		if (editingUserId) {
		(async () => {
			const {user} = await axios.get(`/user/${editingUserId}`);
			setinitialValues({
				_id: editingUserId,
				email: user.email,
				role: user.role,
				firstName: user.firstName || '',
				lastName: user.lastName || '',
				picture_url: user.picture_url || '',
				user_no: user.user_no || '',
				partner_company_name: user.partner_company_name || '',
				partner_IBAN: user.partner_IBAN || '',
				partner_company_address: user.partner_company_address || '',
				partner_hourly_rate: user.partner_hourly_rate || '',
				partner_discount_price: user.partner_discount_price || '',
				partner_vat: user.partner_vat || false,
				partner_company_reg_id: user.partner_company_reg_id || '',
				partner_company_vat_id: user.partner_company_vat_id || '',
				// Business client pricing
				business_client: user.business_client || false,
				business_hourly_rate: user.business_hourly_rate || '',
				business_discount_price: user.business_discount_price || '',
				// Matchmaking fields
				legal_fields: user.legal_fields || [],
				services_offered: user.services_offered || [],
				languages: user.languages || [],
				office_locations: user.office_locations || '',
				is_active_for_matching: user.is_active_for_matching || false,
				lawyer_level: user.lawyer_level || 'pro',
			});
			if (user.role === 'lawyer' && user.user_no) {
				try {
					const { profile } = await axios.get(`/match/profiles/${user.user_no}`);
					setMatchProfile({
						expertise: (profile?.expertise || []).map((item) => ({
							field_id: Number(item.field_id),
							note: item.note || '',
						})),
						feedback_rating: profile?.feedback_rating ?? '',
						feedback_rating_count: profile?.feedback_rating_count ?? '',
						feedback_rating_source: profile?.feedback_rating_source || 'sync',
						feedback_rating_synced_at: profile?.feedback_rating_synced_at || null,
					});
				} catch (e) {
					setMatchProfile({
			expertise: [],
			feedback_rating: '',
			feedback_rating_count: '',
			feedback_rating_source: 'sync',
			feedback_rating_synced_at: null,
		});
				}
			}
		})();
		}
	}, [editingUserId]);
	
	// Fetch legal fields on mount
	useEffect(() => {
		(async () => {
			try {
				const response = await axios.get('/legal-fields');
				const fieldsData = response.data || response;
				setLegalFields(Array.isArray(fieldsData) ? fieldsData.filter(field => field.is_active) : []);
			} catch (error) {
				logger.error('Error fetching legal fields:', error);
				setLegalFields([]);
			}
		})();
	}, []);


	
	/* Auto-update lawyers.json when user profile changes */
	const updateLawyerProfileData = async (userId) => {
		try {
			logger.log('=== AUTO-UPDATE LAWYERS.JSON ===');
			logger.log('Updating lawyers.json for user_id:', userId);

			const response = await axios.post('/api/update-lawyer-profile', {
				user_id: userId
			});
			
			logger.log('Lawyer profile data updated in matchmaking system:', response);
			logger.log('=== AUTO-UPDATE COMPLETED ===');
			return true;
		} catch (error) {
			logger.error('Error updating lawyer profile in matchmaking system:', error);
			// Don't show error to user, just log it
			return false;
		}
	};
	
	/* Table actions */
	
	const onSuccess = async (response) => {
		setrefetch(false);
		setErrorMessage(''); // Clear any error messages
		if (editingUserId) {
			// Auto-update lawyers.json for lawyer/admin users
			const savedUser = response?.user || response;
			if (savedUser && (savedUser.role === 'lawyer' || savedUser.role === 'admin')) {
				logger.log('User saved, triggering auto-update for lawyers.json...');
				await updateLawyerProfileData(editingUserId);
			}
			if (savedUser?.role === 'lawyer' && savedUser.user_no) {
				const currentProfile = matchProfileRef.current;
				try {
					const expertise = normalizeExpertiseForSave(currentProfile.expertise);
					const invalidNote = expertise.some((item) => !isExpertiseNoteValid(item.note));
					if (invalidNote) {
						notifyError(t('match.expertiseNoteLengthError'));
						logger.error('Match profile save skipped: invalid expertise note length');
						return;
					}
					const result = await axios.put(`/match/profiles/${savedUser.user_no}`, {
						expertise,
						feedback_rating: currentProfile.feedback_rating !== '' ? Number(currentProfile.feedback_rating) : undefined,
						feedback_rating_count: currentProfile.feedback_rating_count !== '' ? Number(currentProfile.feedback_rating_count) : undefined,
					});
					if (result?.status >= 400 || result?.error) {
						notifyError(result.message || t('match.saveFailed') || 'Failed to save expertise');
						logger.error('Failed to save match profile:', result);
						return;
					}
				} catch (e) {
					notifyError(e.message || t('match.saveFailed') || 'Failed to save expertise');
					logger.error('Failed to save match profile:', e);
					return;
				}
			}
			
			setrefetch(true);
			setEditingUserId(null); // Reset editing user ID
			closeModal(); // Close modal on successful edit
		} else {
			setrefetch(true);
		}
	};
	
	const onError = (error) => {
		logger.error('Form submission error:', error);
		if (error.response && error.response.data && error.response.data.message) {
			setErrorMessage(error.response.data.message);
		} else if (error.message) {
			setErrorMessage(error.message);
		} else {
			setErrorMessage('An error occurred while saving the user');
		}
	};
	
	const onValidationError = (validationErrors) => {
		logger.error('Validation errors:', validationErrors);
		if (validationErrors && validationErrors.length > 0) {
			setErrorMessage(validationErrors.map(err => err.message).join(', '));
		} else {
			setErrorMessage('Please check the form for validation errors');
		}
	};
	
	const onEdit = ({_id}) => {
		setNewUser(false);
		setEditingUserId(_id);
		openModal();
	};
	
	const onDelete = ({_id, firstName, lastName}) => {
		setrefetch(false);
		window.nConfirm({
			icon: 'fa-question-circle',
			title: 'Delete user?',
			text: `<span>${firstName} ${lastName}</span>`,
			onYes: () => { deleteUser(_id) },
			onNo:  () => {}
		});
	};




	


	const deleteUser = async (id) => {
		await axios.delete(`/user/${id}`);
		setrefetch(true);
	};
	
	const actions = (cell, row, rowIndex, formatExtraData) => {
		return (
			<div className="row row-cols-auto">
				<h6>
					<span onClick={() => { logger.log('Accept') }} className="mx-1 badge bg-primary">
						Accept
					</span>
				</h6>
				<h6>
					<span onClick={() => { logger.log('Reject') }} className="mx-1 badge bg-danger">
						Reject
					</span>
				</h6>
			</div>
		);
	};
	
	/* Updated table columns: using fullName, and adding user_no */
	
	const tableColumns = [
		{
			dataField: 'lawyer_level',
			text: 'Pro',
			classes: 'cell-pro',
			headerClasses: 'cell-pro',
			style: { width: '70px' },
			headerStyle: { width: '70px' },
			formatter: (level, r) => {
				// Only show for lawyers
				if (r.role !== 'lawyer') {
					return <div className="inner">—</div>;
				}
				
				const isPro = level === 'pro';
				
				return (
					<div className="inner" style={{display: 'flex', justifyContent: 'center'}}>
						<div 
							className="btn-switch" 
							onClick={async (e) => {
								e.stopPropagation();
								const newLevel = isPro ? 'basic' : 'pro';
								
								logger.log('=== UPDATING LAWYER LEVEL ===');
								logger.log('User ID:', r._id);
								logger.log('Current level:', level);
								logger.log('New level:', newLevel);
								
								try {
									// Update the lawyer level
									const response = await axios.put(`/user/${r._id}`, {
										_id: r._id,
										email: r.email,
										role: r.role,
										firstName: r.firstName,
										lastName: r.lastName,
										lawyer_level: newLevel
									});
									
									logger.log('Update response:', response);
									
									// Reset refetch to trigger data reload
									setrefetch(false);
									setTimeout(() => {
										setrefetch(true);
									}, 100);
									
									window.nSuccess({
										title: 'Success!',
										text: `Lawyer level updated to ${newLevel}`,
										icon: 'fa-check-circle',
									});
								} catch (error) {
									logger.error('=== ERROR UPDATING LAWYER LEVEL ===');
									logger.error('Error:', error);
									logger.error('Error response:', error.response?.data);
									window.nError({
										title: 'Error',
										text: error.response?.data?.message || 'Failed to update lawyer level',
										icon: 'fa-exclamation-circle',
									});
								}
							}}
						>
							<div className={`switch ${isPro ? 'on' : 'off'}`}>
								<span></span>
							</div>
						</div>
					</div>
				);
			}
		},
		{
			dataField: 'fullName',
			text: 'Name',
			classes: 'cell-name',
			headerClasses: 'cell-name',
			formatter: (c, r) => {
				const fullName = `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim();
				const isActive = r.is_active_for_matching;
				
				// Build tooltip parts conditionally
				
				let tooltipText = `<i class='fa-solid fa-user-tie'></i><label>${fullName}</label>`;
				
				tooltipText += `<span class="role">Role: ${r.role}</span>`;
				
				if (r.user_no) {
					tooltipText += `<span>CRM: ${r.user_no}</span>`;
				}
				
				return (
					<div
						className='inner clickable-name'
						onClick={() => onEdit({_id: r._id})}
						onMouseEnter={e =>
							setTooltip({
								visible: true,
								text: tooltipText,
								x: e.clientX,
								y: e.clientY
							})
						}
						onMouseMove={e => setTooltip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
						onMouseLeave={() => setTooltip(t => ({ ...t, visible: false }))}
						style={{display: 'flex', alignItems: 'center'}}
						>
						{isActive && (
							<span 
								style={{
									display: 'inline-block',
									width: '8px',
									height: '8px',
									borderRadius: '50%',
									backgroundColor: '#28a745',
									marginRight: '8px',
									flexShrink: 0
								}}
							></span>
						)}
						<span>{fullName}</span>
					</div>
				);
			}
		},
		/*
		{
			dataField: 'user_no',
			text: 'CRM ID',
			classes: 'cell-user',
			headerClasses: 'cell-user',
		},
		*/
		{
			dataField: 'email',
			text: 'Email',
			classes: 'cell-email',
			headerClasses: 'cell-email',
			formatter: (email, r) => <a href={`mailto:${email}`} title="Send e-mail">{email}</a>,
		},
		{
			dataField: 'partner_company_name',
			text: 'Company Name',
			classes: 'cell-company',
			headerClasses: 'cell-company',
		},
		{
			dataField: 'user_no',
			text: 'CRM ID',
			classes: 'cell-crm',
			headerClasses: 'cell-crm',
		},
		{
			dataField: 'partner_hourly_rate',
			text: 'Hourly Rate',
			classes: 'cell-rate',
			headerClasses: 'cell-rate',
			formatter: (rate) => rate ? `€${rate}` : '-',
		},
		{
			dataField: 'partner_vat',
			text: 'VAT',
			classes: 'cell-vat',
			headerClasses: 'cell-vat',
			formatter: (vat) => vat ? 'Yes' : '-',
		},
		/*
		{
			dataField: 'role',
			text: 'Role',
			classes: 'cell-role',
			headerClasses: 'cell-role',
		},
		*/
		{
			dataField: 'createdAt',
			text: 'Created',
			classes: 'cell-created',
			headerClasses: 'cell-created',
			formatter: d => formatDateDMY(d),
		},
	];
	
	const userRequestColumns = [
		{
			dataField: 'firstName',
			text: 'Name',
			classes: 'cell-name',
			headerClasses: 'cell-name',
			formatter: (v, {firstName, lastName}) => {
				return `${firstName ?? ''} ${lastName ?? ''}`;
			},
		},
		{
			dataField: 'email',
			text: 'Email',
			classes: 'cell-email',
			headerClasses: 'cell-email',
			formatter: (email, r) => <a href={`mailto:${email}`} title="Send e-mail">{email}</a>,
		},
		{
			dataField: 'request',
			text: 'Request Message',
			classes: 'cell-request',
			headerClasses: 'cell-request',
		},
		{
			dataField: 'createdAt',
			text: 'Requested At',
			classes: 'cell-created',
			headerClasses: 'cell-created',
			formatter: d => formatDateDMY(d),
		},
	];
	
	const userRequestActions = {
		dataField: '',
		text: 'Actions',
		classes: 'cell-actions',
		headerClasses: 'cell-actions',
		formatter: actions,
	};
	
	/* Render HTML */
	
	return (
	<>
		<div id="users" className="row table_v2 users">
			<div className="col p-0">
				<div className="x_panel chatbot-builder users-page">
					<div className="header-menu d-flex justify-content-between align-items-center">
						<div className="header-title d-flex align-items-center">
							<h1>Users</h1>
						</div>
						<div className="header-actions d-flex align-items-center">
							
							<div className="action action-addnew">
								<button className="button button-blue" onClick={addUser}>
									+ Add New
								</button>
							</div>
							<div className="action action-update">
								<UpdateLawyersDataButton />
							</div>
						</div>
					</div>
					<div className="x_content">
						<div className="page-container d-flex flex-column">
							<div className="page-content">
								<DataTable
								dataKey      = "users"
								tableHeading = "Users"
								url          ={`/user/all${search ? `?search=${encodeURIComponent(search)}` : ''}`}
								columns      = {tableColumns}
								refetch      = {refetch}
								search       = {false}
								onDelete     = {onDelete}
								onEdit       = {onEdit}
								rowClasses = {(row, rowIndex) => {
									return 'table-row';
								}}
								rowStyle   = {(row, rowIndex) => {
									const isActive = row.is_active_for_matching;
									return isActive ? { backgroundColor: '#f0f7ff' } : {};
								}}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* Modal */}
			{isModalOpen && (
				<div className="modal">
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span className="modalclose" onClick={closeModal}>&times;</span>
							<h2>{newUser ? 'New user' : 'Edit user'}</h2>
							
							
						</div>
						<div className="modal-form">
							<Form 
								url="/user" 
								usePUT={editingUserId ? true : false} 
								initialValues={initialValues} 
								onSuccess={onSuccess}
								onError={onError}
								onValidationError={onValidationError}
							>
								{/* Error message display */}
								{errorMessage && (
									<div className="form-row col-full">
										<div className="alert alert-danger">
											{errorMessage}
										</div>
									</div>
								)}
								<div className="form-row col-full row-label">
									<h3>User details</h3>
								</div>
								
								{/* Picture Preview - First after heading */}
								<Field name="picture_url">
									{({ field }) => (
										<>
											{field.value && (
												<div className="form-row col-full">
													<img 
														src={field.value} 
														alt="Profile Picture Preview" 
														style={{
															width: '200px', 
															height: '200px', 
															objectFit: 'cover', 
															borderRadius: '8px',
															border: '1px solid #ddd',
															marginTop: '10px'
														}}
														onError={(e) => {
															e.target.style.display = 'none';
														}}
													/>
												</div>
											)}
										</>
									)}
								</Field>
								
								<div className="form-row col-half row-firstname">
									<Field name="firstName">
										{({field, form}) => (
											<>
												<input
													{...field}
													type="text"
													placeholder="First Name *"
													autoComplete="off"
													style={{width: '100%', borderColor: validationErrors.firstName ? '#dc3545' : undefined}}
												/>
												{validationErrors.firstName && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.firstName}
													</div>
												)}
											</>
										)}
									</Field>
								</div>
								<div className="form-row col-half row-lastname">
									<Field name="lastName">
										{({field, form}) => (
											<>
												<input
													{...field}
													type="text"
													placeholder="Last Name *"
													autoComplete="off"
													style={{width: '100%', borderColor: validationErrors.lastName ? '#dc3545' : undefined}}
												/>
												{validationErrors.lastName && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.lastName}
													</div>
												)}
											</>
										)}
									</Field>
								</div>
								
								{/* Email and Role on same row */}
								<div className="form-row col-half row-email">
									<Field name="email">
										{({field, form}) => (
											<>
												<input
													{...field}
													type="text"
													placeholder="E-mail *"
													autoComplete="email"
													style={{width: '100%', borderColor: (emailError || validationErrors.email) ? '#dc3545' : undefined}}
												/>
												{(emailError || validationErrors.email) && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{emailError || validationErrors.email}
													</div>
												)}
											</>
										)}
									</Field>
								</div>
								<div className="form-row col-half row-role">
									<Field name="role">
										{({field, form}) => (
											<>
												<Select
													id       = "role"
													name     = {field.name}
													value    = {roles.find((i) => i.value === form.values.role)}
													options  = {roles}
													placeholder="Role *"
													onChange = {(option) => {form.setFieldValue(field.name, option.value)}}
												/>
												{validationErrors.role && (
													<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
														{validationErrors.role}
													</div>
												)}
											</>
										)}
									</Field>
								</div>
								
								{/* Picture URL Input - After email and role */}
								<div className="form-row col-full">
									<Field
									as           = "input"
									type         = "text"
									id           = "picture_url"
									name         = "picture_url"
									placeholder  = "Picture URL"
									autoComplete = "off"
									/>
								</div>
								
								{/* CRM ID field - last in section, only show in edit mode */}
								{!newUser && (
									<div className="form-row col-half row-crmid" style={{marginBottom: '20px'}}>
										<Field name="user_no">
											{({field}) => (
												<div style={{fontSize: '13px', textAlign: 'left'}}>
													CRM ID: {field.value || 'Not assigned'}
												</div>
											)}
										</Field>
									</div>
								)}
								<div className="form-row col-full row-label">
									<h3>Company details</h3>
								</div>
								<Field name="role">
									{({field: roleField}) => (
										<>
											<div className="form-row col-half row-name">
												<Field name="partner_company_name">
													{({field, form}) => (
														<>
															<input
																{...field}
																type="text"
																placeholder={(roleField.value === 'lawyer' || roleField.value === 'admin') ? "Company Name *" : "Company Name"}
																autoComplete="off"
																style={{width: '100%', borderColor: validationErrors.partner_company_name ? '#dc3545' : undefined}}
															/>
															{validationErrors.partner_company_name && (
																<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																	{validationErrors.partner_company_name}
																</div>
															)}
														</>
													)}
												</Field>
											</div>
											<div className="form-row col-half row-address">
												<Field name="partner_company_address">
													{({field, form}) => (
														<>
															<input
																{...field}
																type="text"
																placeholder={(roleField.value === 'lawyer' || roleField.value === 'admin') ? "Company Address *" : "Company Address"}
																autoComplete="off"
																style={{width: '100%', borderColor: validationErrors.partner_company_address ? '#dc3545' : undefined}}
															/>
															{validationErrors.partner_company_address && (
																<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																	{validationErrors.partner_company_address}
																</div>
															)}
														</>
													)}
												</Field>
											</div>
											<div className="form-row col-half row-id">
												<Field name="partner_company_reg_id">
													{({field, form}) => (
														<>
															<input
																{...field}
																type="text"
																placeholder={(roleField.value === 'lawyer' || roleField.value === 'admin') ? "Registration Code *" : "Registration Code"}
																autoComplete="off"
																style={{width: '100%', borderColor: validationErrors.partner_company_reg_id ? '#dc3545' : undefined}}
															/>
															{validationErrors.partner_company_reg_id && (
																<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																	{validationErrors.partner_company_reg_id}
																</div>
															)}
														</>
													)}
												</Field>
											</div>
											<div className="form-row col-half row-iban">
												<Field name="partner_IBAN">
													{({field, form}) => (
														<>
															<input
																{...field}
																type="text"
																placeholder={(roleField.value === 'lawyer' || roleField.value === 'admin') ? "IBAN *" : "IBAN"}
																autoComplete="off"
																style={{width: '100%', borderColor: validationErrors.partner_IBAN ? '#dc3545' : undefined}}
															/>
															{validationErrors.partner_IBAN && (
																<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																	{validationErrors.partner_IBAN}
																</div>
															)}
														</>
													)}
												</Field>
											</div>
											<div className="form-row col-half row-rate">
												<Field name="partner_hourly_rate">
													{({field, form}) => (
														<>
															<input
																{...field}
																type="text"
																placeholder={(roleField.value === 'lawyer' || roleField.value === 'admin') ? "Hourly rate for individual clients *" : "Hourly rate for individual clients"}
																autoComplete="off"
																style={{width: '100%', borderColor: validationErrors.partner_hourly_rate ? '#dc3545' : undefined}}
															/>
															{validationErrors.partner_hourly_rate && (
																<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																	{validationErrors.partner_hourly_rate}
																</div>
															)}
														</>
													)}
												</Field>
											</div>
											<div className="form-row col-half row-discount-price">
												<Field name="partner_discount_price">
													{({field, form}) => (
														<>
															<input
																{...field}
																type="text"
																placeholder="Discount for 30-min initial contact for individual clients: (optional)"
																autoComplete="off"
																style={{width: '100%', borderColor: validationErrors.partner_discount_price ? '#dc3545' : undefined}}
															/>
															{validationErrors.partner_discount_price && (
																<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																	{validationErrors.partner_discount_price}
																</div>
															)}
														</>
													)}
												</Field>
											</div>
										</>
									)}
								</Field>
								{/* Business Client Toggle and Pricing - On separate line after hourly rate lines */}
								<Field name="business_client">
									{({field: businessField, form}) => (
										<>
											<div className="form-row col-full" style={{marginTop: '20px', marginBottom: businessField.value ? '0' : '20px'}}>
												<div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
													<span style={{fontSize: '13px', color: '#666'}}>Serve Business Clients:</span>
													<div className="btn-switch" onClick={() => form.setFieldValue('business_client', !businessField.value)}>
														<div className={`switch ${businessField.value ? 'on' : 'off'}`}>
															<span></span>
														</div>
													</div>
												</div>
											</div>
											{businessField.value && (
												<>
													<div className="form-row col-half">
														<Field name="business_hourly_rate">
															{({field, form}) => (
																<>
																	<input
																		{...field}
																		type="text"
																		placeholder={(form.values.role === 'lawyer' || form.values.role === 'admin') ? "Hourly rate for businesses *" : "Hourly rate for businesses"}
																		autoComplete="off"
																		style={{width: '100%', borderColor: validationErrors.business_hourly_rate ? '#dc3545' : undefined}}
																	/>
																	{validationErrors.business_hourly_rate && (
																		<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																			{validationErrors.business_hourly_rate}
																		</div>
																	)}
																</>
															)}
														</Field>
													</div>
													
													<div className="form-row col-half">
														<Field name="business_discount_price">
															{({field, form}) => (
																<>
																	<input
																		{...field}
																		type="text"
																		placeholder="Discount for 30-min initial contact for businesses: (optional)"
																		autoComplete="off"
																		style={{width: '100%', borderColor: validationErrors.business_discount_price ? '#dc3545' : undefined}}
																	/>
																	{validationErrors.business_discount_price && (
																		<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																			{validationErrors.business_discount_price}
																		</div>
																	)}
																</>
															)}
														</Field>
													</div>
												</>
											)}
										</>
									)}
								</Field>
								{/* VAT Toggle and VAT ID - On separate line after prices */}
								<div className="form-row col-full row-vat" style={{marginTop: '20px'}}>
									<Field name="partner_vat">
										{({field: vatField, form}) => (
											<>
												<div style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: vatField.value ? '15px' : '0'}}>
													<span style={{fontSize: '13px', color: '#666'}}>VAT Applied:</span>
													<div className="btn-switch" onClick={() => form.setFieldValue('partner_vat', !vatField.value)}>
														<div className={`switch ${vatField.value ? 'on' : 'off'}`}>
															<span></span>
														</div>
													</div>
												</div>
												{vatField.value && (
													<div>
														<Field name="partner_company_vat_id">
															{({field, form}) => (
																<>
																	<input
																		{...field}
																		type="text"
																		placeholder="VAT ID *"
																		style={{width: '100%', borderColor: validationErrors.partner_company_vat_id ? '#dc3545' : undefined}}
																	/>
																	{validationErrors.partner_company_vat_id && (
																		<div style={{color: '#dc3545', fontSize: '12px', marginTop: '4px'}}>
																			{validationErrors.partner_company_vat_id}
																		</div>
																	)}
																</>
															)}
														</Field>
													</div>
												)}
											</>
										)}
									</Field>
								</div>
								
								{/* Matchmaking Settings - Only for Lawyers */}
								<Field name="role">
								{({field}) => (
									field.value === 'lawyer' && (
										<>
											<div className="form-row col-full row-label" style={{marginTop: '30px'}}>
												<h3>Matchmaking Settings</h3>
												<p style={{fontSize: '14px', color: '#666', marginTop: '5px'}}>
													Configure settings for client-lawyer matching. These settings are used when clients request lawyer recommendations.
												</p>
											</div>
											
											{/* Active for Matching */}
											<Field name="is_active_for_matching">
											{({field, form}) => (
												<div className="form-row col-full" style={{marginTop: '20px'}}>
													<div style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: validationErrors.is_active_for_matching ? '5px' : '0'}}>
														<div className="btn-switch" onClick={() => {
															const newValue = !field.value;
															form.setFieldValue('is_active_for_matching', newValue);
															// Clear validation error when unchecked
															if (!newValue && validationErrors.is_active_for_matching) {
																setValidationErrors(prev => {
																	const newErrors = { ...prev };
																	delete newErrors.is_active_for_matching;
																	return newErrors;
																});
															}
														}}>
															<div className={`switch ${field.value ? 'on' : 'off'}`}>
																<span></span>
															</div>
														</div>
														<span style={{fontSize: '13px', color: '#666'}}><strong>Active for Matchmaking</strong></span>
													</div>
													{validationErrors.is_active_for_matching && (
														<div style={{
															marginTop: '5px',
															fontSize: '12px',
															color: '#dc3545'
														}}>
															{validationErrors.is_active_for_matching}
														</div>
													)}
												</div>
											)}
											</Field>
											
											{/* Legal Fields */}
											<Field name="legal_fields">
											{({field, form}) => (
												<div className="form-row col-full" style={{marginTop: '20px'}}>
													<label>Legal Fields ({field.value?.length || 0} / 15)</label>
													<div>
														{legalFields.length > 0 ? (
															<div className="accordion">
																{(() => {
																	// Group legal fields by category
																	const groupedFields = legalFields
																		.sort((a, b) => a.order - b.order)
																		.reduce((groups, field) => {
																			const category = field.category || 'Other';
																			if (!groups[category]) {
																				groups[category] = [];
																			}
																			groups[category].push(field);
																			return groups;
																		}, {});

																	// Sort categories: predefined categories first, then "Other"
																	const predefinedCategories = LEGAL_FIELD_CATEGORIES;
																	const sortedCategories = [
																		...predefinedCategories.filter(cat => groupedFields[cat]),
																		...Object.keys(groupedFields).filter(cat => !predefinedCategories.includes(cat)).sort()
																	];

																	return sortedCategories.map((category, categoryIndex) => (
																		<div key={category} className="panel">
																			<div className="panel-heading" style={{cursor: 'pointer', background: 'none', border: 'none', paddingLeft: 0}} onClick={() => {
																				const content = document.getElementById(`legal-fields-${categoryIndex}`);
																				const icon = document.getElementById(`legal-fields-icon-${categoryIndex}`);
																				if (content.style.display === 'none') {
																					content.style.display = 'block';
																					icon.className = 'fa fa-chevron-up';
																				} else {
																					content.style.display = 'none';
																					icon.className = 'fa fa-chevron-down';
																				}
																			}}>
																				<h4 style={{margin: 0, fontSize: '14px', fontWeight: 'normal'}}>
																					{category}
																					<span style={{float: 'right', fontSize: '12px', color: '#666'}}>
																						{groupedFields[category].length} field{groupedFields[category].length !== 1 ? 's' : ''}
																					</span>
																					<i id={`legal-fields-icon-${categoryIndex}`} className="fa fa-chevron-down" style={{float: 'right', marginRight: '10px'}}></i>
																				</h4>
																			</div>
																			<div id={`legal-fields-${categoryIndex}`} style={{display: 'none', padding: '10px 0', textAlign: 'left'}}>
																				<ul style={{listStyle: 'none', padding: 0, margin: 0, textAlign: 'left'}}>
																					{groupedFields[category].map(legalField => (
																						<li key={legalField.field_id} style={{padding: '8px 0', textAlign: 'left', display: 'flex', alignItems: 'center'}}>
																							<label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0, textAlign: 'left', justifyContent: 'flex-start'}}>
																								<input
																									type="checkbox"
																									className="form-check-input"
																									checked={field.value?.includes(legalField.field_id) || false}
																									onChange={() => {
																										const currentValues = field.value || [];
																										const newValues = currentValues.includes(legalField.field_id)
																											? currentValues.filter(id => id !== legalField.field_id)
																											: [...currentValues, legalField.field_id];
																										form.setFieldValue('legal_fields', newValues);
																									}}
																									style={{margin: 0, width: '22px', height: '22px'}}
																								/>
																								<span style={{fontSize: '13px'}}>{legalField.name_et}</span>
																							</label>
																						</li>
																					))}
																				</ul>
																			</div>
																		</div>
																	));
																})()}
															</div>
														) : (
															<p style={{color: '#999'}}>No legal fields available. Contact admin to add legal fields.</p>
														)}
													</div>
												</div>
											)}
											</Field>
											
											{/* Match v2: Top-5 expertise (admin only) */}
											<Field name="legal_fields">
											{({ field: legalFieldsField }) => (
												<div className="form-row col-full" style={{ marginTop: '20px' }}>
													<label>{t('match.expertiseTop5')} ({matchProfile.expertise?.length || 0} / 5)</label>
													<p style={{ fontSize: '13px', color: '#666' }}>{t('match.expertiseTop5Desc')}</p>
													{validationErrors.expertise && (
														<div style={{
															marginBottom: '8px',
															fontSize: '12px',
															color: '#dc3545'
														}}>
															{validationErrors.expertise}
														</div>
													)}
													<div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px' }}>
														{(legalFieldsField.value || []).map((fieldId) => {
															const fieldIdNum = Number(fieldId);
															const field = legalFields.find((f) => Number(f.field_id) === fieldIdNum);
															const expertise = matchProfile.expertise || [];
															const checked = expertise.some((item) => Number(item.field_id) === fieldIdNum);
															const note = expertise.find((item) => Number(item.field_id) === fieldIdNum)?.note || '';
															const noteLen = note.trim().length;
															const noteInvalid = noteLen > 0 && (noteLen < EXPERTISE_NOTE_MIN || noteLen > EXPERTISE_NOTE_MAX);
															return (
																<div key={fieldId} style={{ marginBottom: '12px' }}>
																	<label style={{ display: 'block', marginBottom: '6px' }}>
																		<input
																			type="checkbox"
																			checked={checked}
																			onChange={() => {
																				const current = matchProfile.expertise || [];
																				let next;
																				if (checked) {
																					next = current.filter((item) => Number(item.field_id) !== fieldIdNum);
																				} else if (current.length >= 5) {
																					notifyError(t('match.expertiseMax5'));
																					return;
																				} else {
																					next = [...current, { field_id: fieldIdNum, note: '' }];
																				}
																				setMatchProfile((p) => ({ ...p, expertise: next }));
																			}}
																		/>
																		{' '}{field?.name_et || fieldId}
																	</label>
																	{checked && (
																		<div style={{ marginLeft: '20px' }}>
																			<label style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>
																				{t('match.expertiseNoteOptional')}
																			</label>
																			<textarea
																				className="form-control"
																				rows={2}
																				value={note}
																				onChange={(e) => {
																					const value = e.target.value;
																					setMatchProfile((p) => ({
																						...p,
																						expertise: (p.expertise || []).map((item) =>
																							Number(item.field_id) === fieldIdNum ? { ...item, note: value } : item
																						),
																					}));
																				}}
																			/>
																			<small style={{ color: noteInvalid ? '#c00' : '#666' }}>
																				{noteLen} / {EXPERTISE_NOTE_MAX}{noteInvalid ? ` — ${t('match.expertiseNoteLengthError')}` : ''}
																			</small>
																		</div>
																	)}
																</div>
															);
														})}
													</div>
													<div className="row mt-3">
														<div className="col-md-3">
															<label>Client feedback rating (0-5)</label>
															<input
																type="number"
																className="form-control"
																min={0}
																max={5}
																step={0.1}
																value={matchProfile.feedback_rating}
																onChange={(e) => setMatchProfile((p) => ({ ...p, feedback_rating: e.target.value }))}
															/>
														</div>
														<div className="col-md-3">
															<label>Feedback count (n)</label>
															<input
																type="number"
																className="form-control"
																min={0}
																value={matchProfile.feedback_rating_count}
																onChange={(e) => setMatchProfile((p) => ({ ...p, feedback_rating_count: e.target.value }))}
															/>
														</div>
														<div className="col-md-3">
															<label>Source</label>
															<input className="form-control" readOnly value={matchProfile.feedback_rating_source || 'sync'} />
															{matchProfile.feedback_rating_synced_at && (
																<small className="text-muted">Last sync: {new Date(matchProfile.feedback_rating_synced_at).toLocaleString()}</small>
															)}
														</div>
														<div className="col-md-3 d-flex align-items-end">
															<button
																type="button"
																className="btn btn-outline-secondary"
																onClick={async () => {
																	try {
																		const { profile } = await axios.put(`/match/profiles/${initialValues.user_no}`, {
																			revert_feedback_rating: true,
																		});
																		setMatchProfile((p) => ({
																			...p,
																			feedback_rating: profile?.feedback_rating ?? '',
																			feedback_rating_count: profile?.feedback_rating_count ?? '',
																			feedback_rating_source: profile?.feedback_rating_source || 'sync',
																			feedback_rating_synced_at: profile?.feedback_rating_synced_at || null,
																		}));
																	} catch (e) {
																		notifyError('Failed to revert feedback rating');
																	}
																}}
															>
																Revert to synced
															</button>
														</div>
													</div>
												</div>
											)}
											</Field>
											
											{/* Services, Locations and Languages */}
											<div className="form-row col-full" style={{marginTop: '20px'}}>
												<label>Services, Locations and Languages</label>
												
												{/* Services Offered */}
												<Field name="services_offered">
												{({field, form}) => (
													<div className="accordion">
														<div className="panel">
															<div className="panel-heading" style={{cursor: 'pointer', background: 'none', border: 'none', paddingLeft: 0}} onClick={() => {
																const content = document.getElementById('services-offered-content');
																const icon = document.getElementById('services-offered-icon');
																if (content.style.display === 'none') {
																	content.style.display = 'block';
																	icon.className = 'fa fa-chevron-up';
																} else {
																	content.style.display = 'none';
																	icon.className = 'fa fa-chevron-down';
																}
															}}>
																<h4 style={{margin: 0, fontSize: '14px', fontWeight: 'normal'}}>
																	Services Offered
																	<span style={{float: 'right', fontSize: '12px', color: '#666'}}>
																		{field.value?.length || 0} selected
																	</span>
																	<i id="services-offered-icon" className="fa fa-chevron-down" style={{float: 'right', marginRight: '10px'}}></i>
																</h4>
															</div>
															<div id="services-offered-content" style={{display: 'none', padding: '10px 0', textAlign: 'left'}}>
																<ul style={{listStyle: 'none', padding: 0, margin: 0, textAlign: 'left'}}>
																	{[
																	{value: 'meeting', label: 'In-person Meeting', disabled: true},
																	{value: 'video', label: 'Video Call', disabled: true},
																	{value: 'web', label: 'Web Consultation'}
																	].map(service => (
																		<li key={service.value} style={{padding: '8px 0', textAlign: 'left', display: 'flex', alignItems: 'center'}}>
																			<label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: service.disabled ? 'not-allowed' : 'pointer', justifyContent: 'flex-start'}}>
																				<input
																					type="checkbox"
																					className="form-check-input"
																					checked={field.value?.includes(service.value) || false}
																					onChange={() => {
																						if (!service.disabled) {
																							const currentValues = field.value || [];
																							const newValues = currentValues.includes(service.value)
																								? currentValues.filter(v => v !== service.value)
																								: [...currentValues, service.value];
																							form.setFieldValue('services_offered', newValues);
																						}
																					}}
																					disabled={service.disabled}
																					style={{margin: 0, width: '22px', height: '22px', opacity: service.disabled ? 0.5 : 1}}
																				/>
																				<span style={{fontSize: '13px', opacity: service.disabled ? 0.5 : 1}}>{service.label}</span>
																			</label>
																		</li>
																	))}
																</ul>
															</div>
														</div>
													</div>
												)}
												</Field>

												{/* Languages */}
												<Field name="languages">
												{({field, form}) => (
													<div className="accordion">
														<div className="panel">
															<div className="panel-heading" style={{cursor: 'pointer', background: 'none', border: 'none', paddingLeft: 0}} onClick={() => {
																const content = document.getElementById('languages-content');
																const icon = document.getElementById('languages-icon');
																if (content.style.display === 'none') {
																	content.style.display = 'block';
																	icon.className = 'fa fa-chevron-up';
																} else {
																	content.style.display = 'none';
																	icon.className = 'fa fa-chevron-down';
																}
															}}>
																<h4 style={{margin: 0, fontSize: '14px', fontWeight: 'normal'}}>
																	Languages
																	<span style={{float: 'right', fontSize: '12px', color: '#666'}}>
																		{field.value?.length || 0} selected
																	</span>
																	<i id="languages-icon" className="fa fa-chevron-down" style={{float: 'right', marginRight: '10px'}}></i>
																</h4>
															</div>
															<div id="languages-content" style={{display: 'none', padding: '10px 0', textAlign: 'left'}}>
																<ul style={{listStyle: 'none', padding: 0, margin: 0, textAlign: 'left'}}>
																	{[
																		{code: 'et', name: 'Estonian'},
																		{code: 'en', name: 'English'},
																		{code: 'ru', name: 'Russian'}
																	].map(lang => (
																		<li key={lang.code} style={{padding: '8px 0', textAlign: 'left', display: 'flex', alignItems: 'center'}}>
																			<label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', justifyContent: 'flex-start'}}>
																				<input
																					type="checkbox"
																					className="form-check-input"
																					checked={field.value?.includes(lang.code) || false}
																					onChange={() => {
																						const currentValues = field.value || [];
																						const newValues = currentValues.includes(lang.code)
																							? currentValues.filter(v => v !== lang.code)
																							: [...currentValues, lang.code];
																						form.setFieldValue('languages', newValues);
																					}}
																					style={{margin: 0, width: '22px', height: '22px'}}
																				/>
																				<span style={{fontSize: '13px'}}>{lang.name}</span>
																			</label>
																		</li>
																	))}
																</ul>
															</div>
														</div>
													</div>
												)}
												</Field>

												{/* Office Locations */}
												<Field name="office_locations">
												{({field, form}) => {
													const availableLocations = [
														'Tallinn', 'Tartu', 'Pärnu', 'Narva', 'Kohtla-Järve',
														'Viljandi', 'Rakvere', 'Maardu', 'Sillamäe', 'Kuressaare',
														'Võru', 'Valga', 'Haapsalu', 'Jõhvi', 'Paide'
													];
													const selectedLocations = field.value ? field.value.split(',').map(l => l.trim()).filter(Boolean) : [];
													
													return (
														<div className="accordion">
															<div className="panel">
																<div className="panel-heading" style={{cursor: 'pointer', background: 'none', border: 'none', paddingLeft: 0}} onClick={() => {
																	const content = document.getElementById('locations-content');
																	const icon = document.getElementById('locations-icon');
																	if (content.style.display === 'none') {
																		content.style.display = 'block';
																		icon.className = 'fa fa-chevron-up';
																	} else {
																		content.style.display = 'none';
																		icon.className = 'fa fa-chevron-down';
																	}
																}}>
																	<h4 style={{margin: 0, fontSize: '14px', fontWeight: 'normal'}}>
																		Office Locations
																		<span style={{float: 'right', fontSize: '12px', color: '#666'}}>
																			{selectedLocations.length} selected
																		</span>
																		<i id="locations-icon" className="fa fa-chevron-down" style={{float: 'right', marginRight: '10px'}}></i>
																	</h4>
																</div>
																<div id="locations-content" style={{display: 'none', padding: '10px 0', textAlign: 'left'}}>
																	<ul style={{listStyle: 'none', padding: 0, margin: 0, textAlign: 'left'}}>
																		{availableLocations.map(location => (
																			<li key={location} style={{padding: '8px 0', textAlign: 'left', display: 'flex', alignItems: 'center'}}>
																				<label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', justifyContent: 'flex-start'}}>
																					<input
																						type="checkbox"
																						className="form-check-input"
																						checked={selectedLocations.includes(location)}
																						onChange={() => {
																							const newLocations = selectedLocations.includes(location)
																								? selectedLocations.filter(l => l !== location)
																								: [...selectedLocations, location];
																							form.setFieldValue('office_locations', newLocations.join(', '));
																						}}
																						style={{margin: 0, width: '22px', height: '22px'}}
																					/>
																					<span style={{fontSize: '13px'}}>{location}</span>
																				</label>
																			</li>
																		))}
																	</ul>
																</div>
															</div>
														</div>
													);
												}}
												</Field>
											</div>
											
										</>
									)
								)}
								</Field>
								
								<CustomSubmitButton 
									newUser={newUser} 
									closeModal={closeModal} 
									editingUserId={editingUserId}
									setEmailError={setEmailError}
									validationErrors={validationErrors}
									setValidationErrors={setValidationErrors}
									matchProfile={matchProfile}
								/>
							</Form>
						</div>
					</div>
				</div>
			)}
			{/* Tooltip */}
			{
				tooltip.visible && (
					<div
					className='d-flex flex-column info-tooltip'
					style={{top: tooltip.y + 10, left: tooltip.x + 10}}
					dangerouslySetInnerHTML={{__html: tooltip.text}}
					/>
				)
			}
		</div>
		
		{User.isAdmin() && (
			<div className="row">
			<div className="col">
				<div className="x_panel">
				<div className="x_title">
					<h2>User Requests</h2>
					<div className="clearfix"></div>
				</div>
				<div className="x_content">
					<DataTable
					url = "/user-request"
					dataKey = "userRequest"
					columns = {userRequestColumns}
					actions = {userRequestActions}
					/>
				</div>
				</div>
			</div>
			</div>
		)}
	</>
	);
};
